import React, { memo } from 'react';
import Div from '@/shared/Div';
import Container from '@/shared/Container';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import Button from '@/shared/Button';
import Map from '@/components/Map';
import withContentFlags from '@/hoc/withContentFlags';
import Image from '@/shared/Image';

interface ListItem {
  id: number;
  text: string;
  subText: string | null;
  icon: {
    data: any;
  };
}

interface Tag {
  id: number;
  text: string;
  subText: string | null;
}

export interface AboutUsProps {
  title: string;
  cta: {
    id: number;
    label: string;
    href: string | null;
    target: string;
    theme: string;
    size: string;
  };
  list: ListItem[];
  tags?: Tag[];
  image?: {
    data: {
      attributes: {
        url: string;
        alternativeText?: string;
      };
    };
  };
  footerText?: string;
}

const AboutUs = (props: AboutUsProps) => {
  const { title, cta, list = [], tags = [], image, footerText } = props;
  const renderListItem = (item: ListItem, index: number) => {
    const { text, subText } = item;

    return (
      <li key={index} className="text-body-lg text-cti-primary">
        {/* {subText} */}
        {subText && <Paragraph className="text-body-lg text-gray-600 mt-1">{subText}</Paragraph>}
      </li>
    );
  };

  return (
    <Div className="bg-cs-secondary py-7 md:py-20">
      <Container>
        <Div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <Div className="flex flex-col gap-s6 flex-1">
            <Heading level="1" className="text-hd-1 md:text-desktop-h-1  text-cm-brand">
              {title}
            </Heading>

            {list.length > 0 && (
              <ul className="flex flex-col gap-4 list-disc pl-6">
                <Map list={list} render={renderListItem} />
              </ul>
            )}

            {cta && (
              <Button
                label={cta.label}
                href={cta.href || undefined}
                target={cta.target}
                theme={cta.theme as any}
                size={cta.size as any}
                dataTestId="about-us-cta"
                rounded
                className="self-start"
              >
                {cta.label}
              </Button>
            )}
          </Div>

          {/* Right Section - Image */}
          <Div className="flex-1 flex items-start justify-center lg:justify-end">
            {image?.data ? (
              <Div className="w-full max-w-md">
                <Image
                  cmsImage={image}
                  // src={image.data.attributes.url}
                  alt={image.data.attributes.alternativeText || 'About Us'}
                  width={500}
                  height={400}
                  className="w-full h-auto object-contain"
                />
              </Div>
            ) : (
              <Div className="w-full max-w-md aspect-[4/3] bg-gray-100 rounded-lg flex items-center justify-center">
                <Paragraph className="text-gray-400">Image placeholder</Paragraph>
              </Div>
            )}
          </Div>
        </Div>

        {/* Footer Tags */}
        {tags.length > 0 && (
          <Div className="mt-12">
            <Paragraph className="text-body-lg text-gray-500 text-center lg:text-left">
              {tags.map((tag) => tag.text).join(' | ')}
            </Paragraph>
          </Div>
        )}

        {/* Footer Text */}
        {footerText && (
          <Div className="mt-12 pt-8 border-t border-gray-200">
            <Paragraph className="text-body-lg text-gray-500 text-center lg:text-left">
              {footerText}
            </Paragraph>
          </Div>
        )}
      </Container>
    </Div>
  );
};

export default withContentFlags(memo(AboutUs));
